<template>
  <BaseFieldset
    v-if="showSettings"
    legend="Product settings"
    class="product-settings"
  >
    <BaseCheckbox
      v-if="showScorable"
      :label="$t('productSettings.STARS_LABEL')"
      :disabled="isEditForm"
      :description="noScoreDescription"
      size="large"
      :checked="!noScoreValue"
      @change="updateNoScore"
    />

    <BaseCheckbox
      v-if="showManagerApproval"
      :label="managerApprovalLabel"
      :disabled="isManagerApprovalDisabled"
      :description="managerApprovalDescription"
      :checked="managerApprovalValue"
      size="large"
      @change="updateManagerApproval"
    />

    <BaseCheckbox
      v-if="showVideoSkip"
      :label="$t('productSettings.WATCH_ENTIRE_VIDEO_LABEL')"
      :description="$t('productSettings.WATCH_ENTIRE_VIDEO_DESCRIPTION')"
      size="large"
      :checked="videoSkipValue"
      @change="updateVideoSkip"
    />
  </BaseFieldset>
</template>

<script>
import BaseCheckbox from '@/components/generics/base/BaseCheckbox.vue'
import BaseFieldset from '@/components/generics/base/BaseFieldset.vue'
import { isNonSimProduct } from '@/helpers/products/productTypes'
import { productTypes } from '@/helpers/products/productTypes/types'
import { mapState } from 'vuex'

export default {
  components: {
    BaseCheckbox,
    BaseFieldset,
  },

  props: {
    productType: {
      type: String,
      required: true,
    },

    isEditForm: {
      type: Boolean,
      required: false,
      default: false,
    },

    noScoreValue: {
      type: Boolean,
      required: false,
      default: false,
    },

    videoSkipValue: {
      type: Boolean,
      required: false,
      default: false,
    },

    managerApprovalValue: {
      type: Boolean,
      required: false,
      default: false,
    },

    isManagerApprovalDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['onUpdateNoScore', 'onUpdateVideoSkip', 'onUpdateManagerApproval'],
  computed: {
    ...mapState({
      currentCompany: (state) => state.common.currentCompany,
    }),

    isActivity() {
      return this.productType === productTypes.ACTIVITY
    },

    isEvent() {
      return this.productType === productTypes.MEET
    },

    isVideo() {
      return this.productType === productTypes.VIDEO
    },

    showScorable() {
      return isNonSimProduct(this.productType)
    },

    showVideoSkip() {
      return (
        this.isVideo &&
        this.currentCompany.feature_flags.includes('enable_no_skip_videos')
      )
    },

    showManagerApproval() {
      return this.isActivity || this.isEvent
    },

    showSettings() {
      return this.showScorable || this.showVideoSkip
    },

    noScoreDescription() {
      return this.isEvent
        ? this.$t('productSettings.STARS_DESCRIPTION_EVENT')
        : this.$t('productSettings.STARS_DESCRIPTION')
    },

    managerApprovalLabel() {
      return this.isEvent
        ? this.$t('productSettings.MANAGER_APPROVAL_LABEL_EVENT')
        : this.$t('productSettings.MANAGER_APPROVAL_LABEL_ACTIVITY')
    },

    managerApprovalDescription() {
      return this.isEvent
        ? this.$t('productSettings.MANAGER_APPROVAL_DESCRIPTION_EVENT')
        : this.$t('productSettings.MANAGER_APPROVAL_DESCRIPTION_ACTIVITY')
    },
  },

  methods: {
    updateManagerApproval(value) {
      this.$emit('onUpdateManagerApproval', value)
    },

    updateNoScore(value) {
      this.$emit('onUpdateNoScore', !value)
    },

    updateVideoSkip(value) {
      this.$emit('onUpdateVideoSkip', value)
    },
  },
}
</script>

<style lang="scss" scoped>
.product-settings {
  display: flex;
  flex-direction: column;
  gap: $spacing-4xs;
}
</style>
